package com.gloopgo.game.engine

import com.gloopgo.game.domain.BlobAiController
import com.gloopgo.game.network.PlayerNetworkState
import kotlin.math.exp
import kotlin.math.max
import kotlin.math.pow
import kotlin.random.Random
import kotlin.uuid.ExperimentalUuidApi
import kotlin.uuid.Uuid

/**
 * Pure Kotlin game engine managing physics, cell movement, collisions, splitting,
 * mass ejection, and bot behaviors. Decoupled from Compose and UI logic.
 */
class GameEngine(
    val worldWidth: Float = GameConstants.WORLD_WIDTH,
    val worldHeight: Float = GameConstants.WORLD_HEIGHT,
    private val random: Random = Random.Default
) {
    private var nextEntityId = 0L
    private fun generateId(prefix: String) = "$prefix-${++nextEntityId}"

    var localPlayer: GloopPlayer? = null

    private val remotePlayers = mutableMapOf<String, GloopPlayer>()
    private val pellets = mutableListOf<FoodPellet>()
    private val ejectedMasses = mutableListOf<EjectedMass>()
    private val newEjectedMasses = mutableListOf<EjectedMass>()
    private val consumedEjectedMassIds = mutableListOf<String>()
    private val acknowledgedEjectedMassIds = mutableSetOf<String>()

    var isGameOver: Boolean = false

    var currentTimestamp: Long = 0L
        private set

    private var lastCameraTarget: Vector2D = Vector2D(worldWidth / 2f, worldHeight / 2f)
    private var lastCameraZoom: Float = 1.0f
    private var lastSplitTimestamp: Long = 0L
    private var lastEjectTimestamp: Long = 0L
    private val consumedPelletIds = mutableListOf<String>()
    private val predationEvents = mutableListOf<PredationEvent>()
    private var hazardPelletConsumed = false

    var isSinglePlayer: Boolean = false
        private set

    private data class AiBotSpec(
        val id: String,
        val name: String,
        val emoji: String,
        val color: Long,
    )

    private val aiSpecs = mutableListOf<AiBotSpec>()
    private val aiRespawnAtMs = mutableMapOf<String, Long>()

    init {
        resetWorld()
    }

    fun resetWorld() {
        pellets.clear()
        remotePlayers.clear()
        ejectedMasses.clear()
        newEjectedMasses.clear()
        consumedEjectedMassIds.clear()
        acknowledgedEjectedMassIds.clear()
        consumedPelletIds.clear()
        predationEvents.clear()
        hazardPelletConsumed = false
        aiSpecs.clear()
        aiRespawnAtMs.clear()
        isGameOver = false
        lastSplitTimestamp = 0L
        lastEjectTimestamp = 0L
        lastCameraTarget = Vector2D(worldWidth / 2f, worldHeight / 2f)
        lastCameraZoom = 1.0f
    }

    fun setSinglePlayerMode(enabled: Boolean) {
        isSinglePlayer = enabled
    }

    /**
     * Seeds 3 local AI opponent blobs + ambient pellets for offline single-player.
     * Call after [spawnLocalPlayer]. Does not touch Firebase / RTDB.
     */
    fun setupSinglePlayerMatch() {
        if (!isSinglePlayer) return
        // Preserve local player; clear any leftover remotes then spawn bots.
        remotePlayers.clear()
        aiSpecs.clear()
        aiRespawnAtMs.clear()
        predationEvents.clear()

        val avoid = mutableListOf(localPlayer?.centerOfGravity ?: Vector2D(worldWidth / 2f, worldHeight / 2f))
        for (i in 0 until BlobAiController.BOT_COUNT) {
            val persona = BlobAiController.BOT_PERSONAS[i % BlobAiController.BOT_PERSONAS.size]
            val spec = AiBotSpec(
                id = "${BlobAiController.AI_BOT_ID_PREFIX}${i + 1}",
                name = persona.name,
                emoji = persona.emoji,
                color = persona.color,
            )
            aiSpecs.add(spec)
            val spawned = spawnAiBot(spec, avoid)
            avoid.add(spawned.centerOfGravity)
        }

        if (pellets.isEmpty()) {
            seedAmbientPellets(GameConstants.PELLET_COUNT)
        }
    }

    private fun spawnAiBot(spec: AiBotSpec, avoid: List<Vector2D>): GloopPlayer {
        val spawn = spawnAwayFrom(avoid)
        val cell = GloopCell(
            id = generateId("ai-cell"),
            position = spawn,
            mass = GameConstants.INITIAL_MASS,
        )
        val bot = GloopPlayer(
            id = spec.id,
            name = spec.name,
            color = spec.color,
            cells = listOf(cell),
            isAlive = true,
            emoji = spec.emoji,
            lastHeartbeat = currentTimestamp,
            joinedAt = currentTimestamp,
        )
        remotePlayers[spec.id] = bot
        aiRespawnAtMs.remove(spec.id)
        return bot
    }

    private fun spawnAwayFrom(avoid: List<Vector2D>): Vector2D {
        var best = getRandomSafeSpawn()
        var bestMin = 0f
        repeat(24) {
            val candidate = getRandomSafeSpawn()
            val minDist = avoid.minOfOrNull { it.distanceTo(candidate) } ?: Float.MAX_VALUE
            if (minDist >= BlobAiController.SPAWN_MIN_SEPARATION) return candidate
            if (minDist > bestMin) {
                bestMin = minDist
                best = candidate
            }
        }
        return best
    }

    private fun seedAmbientPellets(count: Int) {
        repeat(count) {
            val isHazard = random.nextFloat() < GameConstants.HAZARD_SPAWN_CHANCE
            if (isHazard) {
                val emoji = GameConstants.HAZARD_EMOJIS.random(random)
                val color = if (emoji == "💩") GameConstants.HAZARD_COLOR_POOP else GameConstants.HAZARD_COLOR_BOMB
                pellets.add(
                    FoodPellet(
                        id = generateId("sp-pellet"),
                        position = getRandomSafeSpawn(),
                        color = color,
                        mass = GameConstants.HAZARD_PELLET_MASS,
                        emoji = emoji,
                    )
                )
            } else {
                val mass = random.nextInt(1, 11).toFloat()
                val emoji = if (mass >= 10f) GameConstants.FOOD_EMOJIS.random(random) else ""
                pellets.add(
                    FoodPellet(
                        id = generateId("sp-pellet"),
                        position = getRandomSafeSpawn(),
                        color = GameConstants.VIBRANT_COLORS.random(random),
                        mass = mass,
                        emoji = emoji,
                    )
                )
            }
        }
    }

    /**
     * Replaces local pellets with the authoritative pellets from the server / RTDB.
     * Single-player owns pellets entirely in-engine (seed + eat + gradual replenish) —
     * never apply repository / remote snapshots or they wipe/rebuild and flicker.
     */
    fun syncPellets(newPellets: List<FoodPellet>) {
        if (isSinglePlayer) return
        pellets.clear()
        pellets.addAll(newPellets)
    }

    fun syncEjectedMasses(remoteMasses: List<EjectedMass>) {
        val remoteMap = remoteMasses.associateBy { it.id }
        val now = if (currentTimestamp > 0L) currentTimestamp else kotlin.time.Clock.System.now().toEpochMilliseconds()

        for (remote in remoteMasses) {
            acknowledgedEjectedMassIds.add(remote.id)
        }

        // 1. Remove local masses that no longer exist remotely, except locally-created masses
        // whose broadcast is still pending or in-flight.
        val pendingNewIds = newEjectedMasses.map { it.id }.toSet()
        val iterator = ejectedMasses.iterator()
        while (iterator.hasNext()) {
            val local = iterator.next()
            if (local.id in remoteMap) {
                continue
            }
            if (local.id in acknowledgedEjectedMassIds) {
                // Was previously on server, but is now gone -> consumed or pruned
                iterator.remove()
                acknowledgedEjectedMassIds.remove(local.id)
            } else if (local.ownerId == localPlayer?.id && local.id in pendingNewIds) {
                // Locally created, waiting to be broadcast
                continue
            } else if (local.ownerId == localPlayer?.id && local.createdAt > 0L && now - local.createdAt < 2500L) {
                // Locally created, broadcast in-flight
                continue
            } else {
                iterator.remove()
            }
        }

        // 2. Add new remote masses that are not yet tracked locally
        val existingIds = ejectedMasses.map { it.id }.toSet()
        for (remote in remoteMasses) {
            if (remote.id !in existingIds) {
                val elapsed = if (remote.createdAt > 0L) {
                    ((now - remote.createdAt) / 1000f).coerceAtLeast(0f)
                } else {
                    0f
                }
                val decayFactor = exp(-GameConstants.EJECT_DECAY * elapsed)
                val currentVel = remote.velocity * decayFactor
                val displacement = (remote.velocity / GameConstants.EJECT_DECAY) * (1f - decayFactor)
                val currentPos = (remote.position + displacement).let {
                    Vector2D(
                        x = it.x.coerceIn(remote.radius, worldWidth - remote.radius),
                        y = it.y.coerceIn(remote.radius, worldHeight - remote.radius)
                    )
                }

                ejectedMasses.add(
                    remote.copy(
                        position = currentPos,
                        velocity = currentVel
                    )
                )
            }
        }
    }

    fun drainConsumedPelletIds(): List<String> {
        if (consumedPelletIds.isEmpty()) return emptyList()
        val copy = consumedPelletIds.toList()
        consumedPelletIds.clear()
        return copy
    }

    fun drainNewEjectedMasses(): List<EjectedMass> {
        if (newEjectedMasses.isEmpty()) return emptyList()
        val copy = newEjectedMasses.toList()
        newEjectedMasses.clear()
        return copy
    }

    fun drainConsumedEjectedMassIds(): List<String> {
        if (consumedEjectedMassIds.isEmpty()) return emptyList()
        val copy = consumedEjectedMassIds.toList()
        consumedEjectedMassIds.clear()
        return copy
    }

    fun drainHazardPelletConsumed(): Boolean {
        val consumed = hazardPelletConsumed
        hazardPelletConsumed = false
        return consumed
    }

    fun drainPredationEvents(): List<PredationEvent> {
        if (predationEvents.isEmpty()) return emptyList()
        val copy = predationEvents.toList()
        predationEvents.clear()
        return copy
    }

    fun getPellets(): List<FoodPellet> = pellets.toList()

    fun getEjectedMasses(): List<EjectedMass> = ejectedMasses.toList()

    /**
     * Synchronizes remote real players from Firebase RTDB.
     */
    fun syncRemotePlayers(networkStates: List<PlayerNetworkState>, nowTimestamp: Long = kotlin.time.Clock.System.now().toEpochMilliseconds()) {
        // AI bots live entirely in-engine for offline SP — never clobber them with network sync.
        if (isSinglePlayer) return

        val currentRemoteIds = networkStates.map { it.id }.toSet()

        // Remove players who disconnected
        remotePlayers.keys.retainAll(currentRemoteIds)

        // Add or update remote players
        for (state in networkStates) {
            if (state.id == localPlayer?.id) continue // Skip local player
            if (!state.isAlive || state.cells.isEmpty()) {
                remotePlayers.remove(state.id)
                continue
            }

            val playerColor = state.color.toLongOrNull()
                ?: state.color.removePrefix("0x").removePrefix("#").toLongOrNull(16)
                ?: 0xFF00E5FFL

            val existing = remotePlayers[state.id]
            if (existing == null) {
                val newCells = state.cells.map { c ->
                    val pos = Vector2D(c.x, c.y)
                    GloopCell(
                        id = c.id,
                        position = pos,
                        targetPosition = pos,
                        mass = c.mass,
                        targetMass = c.mass
                    )
                }
                remotePlayers[state.id] = GloopPlayer(
                    id = state.id,
                    name = state.name,
                    color = playerColor,
                    cells = newCells,
                    isAlive = state.isAlive,
                    emoji = state.emoji,
                    lastHeartbeat = nowTimestamp,
                    joinedAt = state.joinedAt.toLong()
                )
            } else {
                // Set target network positions & masses for 60 FPS animation loop
                val updatedCells = state.cells.map { targetCell ->
                    val existingCell = existing.cells.find { it.id == targetCell.id }
                    val targetPos = Vector2D(targetCell.x, targetCell.y)
                    existingCell?.copy(
                        targetPosition = targetPos,
                        targetMass = targetCell.mass
                    )
                        ?: GloopCell(
                            id = targetCell.id,
                            position = targetPos,
                            targetPosition = targetPos,
                            mass = targetCell.mass,
                            targetMass = targetCell.mass
                        )
                }
                val updatedEmoji = state.emoji.ifEmpty { existing.emoji }
                remotePlayers[state.id] = existing.copy(
                    color = playerColor,
                    cells = updatedCells,
                    isAlive = state.isAlive,
                    emoji = updatedEmoji,
                    lastHeartbeat = nowTimestamp
                )
            }
        }
    }

    @OptIn(ExperimentalUuidApi::class)
    fun spawnLocalPlayer(
        name: String = "Player",
        playerId: String? = null,
        emoji: String = "",
        color: Long? = null
    ): GloopPlayer {
        val spawnPos = getRandomSafeSpawn()
        val finalColor = color ?: GameConstants.VIBRANT_COLORS.random(random)
        val cell = GloopCell(
            id = generateId("cell"),
            position = spawnPos,
            mass = GameConstants.INITIAL_MASS
        )
        val finalId = playerId ?: "player-${Uuid.random()}"
        val now = if (currentTimestamp > 0L) currentTimestamp else kotlin.time.Clock.System.now().toEpochMilliseconds()
        val player = GloopPlayer(
            id = finalId,
            name = name,
            color = finalColor,
            cells = listOf(cell),
            isAlive = true,
            emoji = emoji,
            lastHeartbeat = now,
            joinedAt = now
        )
        localPlayer = player
        isGameOver = false
        lastCameraTarget = spawnPos
        lastCameraZoom = 1.0f
        return player
    }

    private fun getRandomSafeSpawn(): Vector2D {
        val padding = 200f
        val x = random.nextFloat() * (worldWidth - 2 * padding) + padding
        val y = random.nextFloat() * (worldHeight - 2 * padding) + padding
        return Vector2D(x, y)
    }

    /**
     * Main update tick called each frame.
     * @param deltaTime Elapsed time in seconds since last frame.
     * @param targetWorldPos The world-space coordinates of the mouse pointer or touch point.
     * @param pointerOffset Screen pixel offset of mouse/touch from viewport center (camera target).
     * @param fallbackDirection Optional fallback direction vector (e.g. from keyboard keys).
     * @param timestamp Current epoch or monotonic time in milliseconds.
     */
    fun update(
        deltaTime: Float,
        targetWorldPos: Vector2D? = null,
        pointerOffset: Vector2D? = null,
        fallbackDirection: Vector2D = Vector2D.ZERO,
        timestamp: Long = 0L
    ) {
        currentTimestamp = if (timestamp > 0L) timestamp else kotlin.time.Clock.System.now().toEpochMilliseconds()

        // Heartbeat timeout check: prune remote players who haven't sent updates for > 5 seconds (MP only)
        if (!isSinglePlayer) {
            remotePlayers.entries.removeAll { (_, player) ->
                player.lastHeartbeat > 0L && (currentTimestamp - player.lastHeartbeat > GameConstants.HEARTBEAT_TIMEOUT_MS)
            }
        }

        // 1. Update local player movement based on target pointer position, screen pointer offset, or fallback direction
        localPlayer?.let { player ->
            if (player.isAlive && player.cells.isNotEmpty()) {
                val updatedPlayer = updatePlayerCells(player, targetWorldPos, pointerOffset, fallbackDirection, deltaTime)
                localPlayer = updatedPlayer
                lastCameraTarget = updatedPlayer.centerOfGravity
                val maxExtent = updatedPlayer.boundingRadius
                val rawZoom = GameConstants.BASE_ZOOM / max(30f, maxExtent).pow(GameConstants.ZOOM_EXPONENT) * 3.5f
                lastCameraZoom = rawZoom.coerceIn(GameConstants.MIN_ZOOM, GameConstants.MAX_ZOOM)
            } else if (player.cells.isEmpty() && !isGameOver) {
                isGameOver = true
                localPlayer = player.copy(isAlive = false)
            }
        }

        // 2. Local AI bots (SP) or animate remote players from network targets (MP)
        if (isSinglePlayer) {
            tickLocalAiBots(deltaTime)
        } else {
            updateRemotePlayers(deltaTime)
        }

        // 3. Update ejected mass physics (decay and drift)
        updateEjectedMasses(deltaTime)

        // 4. Handle food pellet collisions
        consumePellets()

        // 5. Handle ejected mass consumption
        consumeEjectedMasses()

        // 6. Handle inter-cell collisions (predation & cell eating)
        handlePredation()

        if (isSinglePlayer) {
            scheduleAiRespawns()
            replenishAmbientPelletsIfNeeded()
        }
    }

    private fun tickLocalAiBots(deltaTime: Float) {
        for (spec in aiSpecs) {
            val bot = remotePlayers[spec.id] ?: continue
            if (!bot.isAlive || bot.cells.isEmpty()) continue

            val others = buildList {
                localPlayer?.let { if (it.isAlive) add(it) }
                remotePlayers.values.filter { it.id != spec.id && it.isAlive }.forEach { add(it) }
            }
            val direction = BlobAiController.chooseDirection(
                self = bot,
                pellets = pellets,
                otherPlayers = others,
                worldWidth = worldWidth,
                worldHeight = worldHeight,
            )
            val updated = updatePlayerCells(
                player = bot,
                targetWorldPos = null,
                pointerOffset = null,
                fallbackDirection = direction,
                deltaTime = deltaTime,
            )
            remotePlayers[spec.id] = updated.copy(lastHeartbeat = currentTimestamp)
        }
    }

    private fun scheduleAiRespawns() {
        for (spec in aiSpecs) {
            val bot = remotePlayers[spec.id]
            if (bot != null && bot.isAlive && bot.cells.isNotEmpty()) continue

            val existing = aiRespawnAtMs[spec.id]
            if (existing == null) {
                // Just died this frame (or missing) — schedule respawn
                if (bot == null || !bot.isAlive || bot.cells.isEmpty()) {
                    aiRespawnAtMs[spec.id] = currentTimestamp + BlobAiController.RESPAWN_DELAY_MS
                    remotePlayers.remove(spec.id)
                }
            } else if (currentTimestamp >= existing) {
                val avoid = mutableListOf(localPlayer?.centerOfGravity ?: Vector2D(worldWidth / 2f, worldHeight / 2f))
                avoid.addAll(remotePlayers.values.filter { it.isAlive }.map { it.centerOfGravity })
                spawnAiBot(spec, avoid)
            }
        }
    }

    private fun replenishAmbientPelletsIfNeeded() {
        val target = GameConstants.PELLET_COUNT
        val missing = target - pellets.size
        if (missing >= 8) {
            seedAmbientPellets(minOf(missing, 20))
        }
    }

    private fun updateRemotePlayers(deltaTime: Float) {
        val posFactor = 1f - exp(-15f * deltaTime)
        val massFactor = 1f - exp(-12f * deltaTime)

        for ((id, player) in remotePlayers) {
            if (!player.isAlive || player.cells.isEmpty()) continue

            val animatedCells = player.cells.map { cell ->
                val dist = cell.position.distanceTo(cell.targetPosition)
                val newPos = if (dist > 500f) {
                    cell.targetPosition
                } else if (dist > 0.1f) {
                    cell.position.lerp(cell.targetPosition, posFactor)
                } else {
                    cell.targetPosition
                }

                val newMass = cell.mass + (cell.targetMass - cell.mass) * massFactor

                cell.copy(
                    position = newPos,
                    mass = newMass
                )
            }

            remotePlayers[id] = player.copy(cells = animatedCells)
        }
    }

    /**
     * Backward-compatible overload for directional vector inputs.
     */
    fun update(deltaTime: Float, movementVector: Vector2D, timestamp: Long) {
        update(deltaTime = deltaTime, targetWorldPos = null, pointerOffset = null, fallbackDirection = movementVector, timestamp = timestamp)
    }

    private fun updatePlayerCells(
        player: GloopPlayer,
        targetWorldPos: Vector2D?,
        pointerOffset: Vector2D?,
        fallbackDirection: Vector2D,
        deltaTime: Float
    ): GloopPlayer {
        val cog = player.centerOfGravity
        val zoom = if (lastCameraZoom > 0.001f) lastCameraZoom else 1.0f

        val updatedCells = player.cells.map { cell ->
            // Impulse velocity decay (from split or recoil)
            val decayedVelocity = cell.velocity * max(0f, 1f - GameConstants.SPLIT_DECAY * deltaTime)

            val (steeringDir, intensity) = if (pointerOffset != null) {
                // Screen pointer offset relative to player center of gravity (camera center).
                // Each split circle calculates its own direction and magnitude to the cursor on screen.
                val cellScreenOffset = (cell.position - cog) * zoom
                val deltaScreen = pointerOffset - cellScreenOffset
                val distPx = deltaScreen.length()
                val deadzone = 12f
                if (distPx > deadzone) {
                    val maxDist = 200f
                    val intensityFactor = ((distPx - deadzone) / (maxDist - deadzone)).coerceIn(0f, 1f)
                    val dir = if (distPx > 0.001f) deltaScreen / distPx else Vector2D.ZERO
                    Pair(dir, intensityFactor)
                } else {
                    Pair(Vector2D.ZERO, 0f)
                }
            } else if (targetWorldPos != null) {
                // World target position fallback (e.g. in unit tests)
                val delta = targetWorldPos - cell.position
                val worldDist = delta.length()
                val screenDist = worldDist * zoom
                val deadzone = 12f
                if (screenDist > deadzone) {
                    val maxDist = 200f
                    val intensityFactor = ((screenDist - deadzone) / (maxDist - deadzone)).coerceIn(0f, 1f)
                    val dir = if (worldDist > 0.001f) delta / worldDist else Vector2D.ZERO
                    Pair(dir, intensityFactor)
                } else {
                    Pair(Vector2D.ZERO, 0f)
                }
            } else {
                // Directional steering (e.g. keyboard arrows or joystick vector)
                val intensityFactor = fallbackDirection.length().coerceIn(0f, 1f)
                val dir = if (intensityFactor > 0.001f) fallbackDirection.normalized() else Vector2D.ZERO
                Pair(dir, intensityFactor)
            }

            // Individual split circle velocity heading towards cursor
            val steeringVelocity = steeringDir * (cell.speed * intensity)
            val effectiveVelocity = steeringVelocity + decayedVelocity

            var newPos = cell.position + effectiveVelocity * deltaTime

            // World boundary clamping
            val radius = cell.radius
            newPos = Vector2D(
                x = newPos.x.coerceIn(radius, worldWidth - radius),
                y = newPos.y.coerceIn(radius, worldHeight - radius)
            )

            cell.copy(position = newPos, velocity = decayedVelocity)
        }.toMutableList()

        // Self-cell collisions: repulsion when separated or recombining when cooldown expired
        resolveSelfCellCollisions(updatedCells)

        return player.copy(cells = updatedCells)
    }

    private fun resolveSelfCellCollisions(cells: MutableList<GloopCell>) {
        var i = 0
        while (i < cells.size) {
            var j = i + 1
            while (j < cells.size) {
                val cellA = cells[i]
                val cellB = cells[j]
                val dist = cellA.position.distanceTo(cellB.position)
                val sumRadius = cellA.radius + cellB.radius

                val canRecombine = currentTimestamp >= cellA.canRecombineAt && currentTimestamp >= cellB.canRecombineAt

                if (canRecombine) {
                    // Once cooldown elapsed, cells consume each other when touching/overlapping borders!
                    if (dist <= sumRadius) {
                        val newMass = cellA.mass + cellB.mass
                        val newPos = (cellA.position * cellA.mass + cellB.position * cellB.mass) / newMass
                        val newRecombineAt = minOf(cellA.canRecombineAt, cellB.canRecombineAt)
                        cells[i] = cellA.copy(mass = newMass, position = newPos, canRecombineAt = newRecombineAt)
                        cells.removeAt(j)
                        continue
                    }
                    // When eligible to recombine, do NOT push cells apart, allowing player to steer and combine them!
                } else if (dist < sumRadius && dist > 0.001f) {
                    // Push apart while cooldown is still active
                    val overlap = sumRadius - dist
                    val pushDir = (cellA.position - cellB.position).normalized()
                    val pushAmount = pushDir * (overlap * 0.5f)
                    cells[i] = cellA.copy(position = cellA.position + pushAmount)
                    cells[j] = cellB.copy(position = cellB.position - pushAmount)
                }
                j++
            }
            i++
        }
    }

    private fun updateEjectedMasses(deltaTime: Float) {
        val iterator = ejectedMasses.listIterator()
        while (iterator.hasNext()) {
            val mass = iterator.next()
            val decayedVel = mass.velocity * max(0f, 1f - GameConstants.EJECT_DECAY * deltaTime)
            var newPos = mass.position + decayedVel * deltaTime

            val r = mass.radius
            newPos = Vector2D(
                x = newPos.x.coerceIn(r, worldWidth - r),
                y = newPos.y.coerceIn(r, worldHeight - r)
            )

            iterator.set(mass.copy(position = newPos, velocity = decayedVel))
        }
    }



    private fun consumePellets() {
        val allCells = mutableListOf<Pair<GloopPlayer, GloopCell>>()
        localPlayer?.let { p -> p.cells.forEach { c -> allCells.add(p to c) } }
        remotePlayers.values.forEach { r -> r.cells.forEach { c -> allCells.add(r to c) } }

        if (allCells.isEmpty()) return

        val pelletIterator = pellets.iterator()
        while (pelletIterator.hasNext()) {
            val pellet = pelletIterator.next()
            for ((player, cell) in allCells) {
                val reach = cell.radius + pellet.radius
                if (cell.position.distanceSquaredTo(pellet.position) <= reach * reach) {
                    growCell(player.id, cell.id, pellet.effectiveMass)
                    pelletIterator.remove()
                    if (player.id == localPlayer?.id || isSinglePlayer) {
                        consumedPelletIds.add(pellet.id)
                        if (player.id == localPlayer?.id && pellet.isHazard) {
                            hazardPelletConsumed = true
                        }
                    }
                    break
                }
            }
        }
    }

    private fun consumeEjectedMasses() {
        val allCells = mutableListOf<Pair<GloopPlayer, GloopCell>>()
        localPlayer?.let { p -> p.cells.forEach { c -> allCells.add(p to c) } }
        remotePlayers.values.forEach { r -> r.cells.forEach { c -> allCells.add(r to c) } }

        val massIterator = ejectedMasses.iterator()
        while (massIterator.hasNext()) {
            val ejected = massIterator.next()
            for ((player, cell) in allCells) {
                val reach = cell.radius + ejected.radius
                // Must be slightly larger than the ejected mass to eat it, and impact occurs on border touch
                if (cell.radius > ejected.radius &&
                    cell.position.distanceSquaredTo(ejected.position) <= reach * reach
                ) {
                    growCell(player.id, cell.id, ejected.mass)
                    massIterator.remove()
                    if (player.id == localPlayer?.id) {
                        consumedEjectedMassIds.add(ejected.id)
                    }
                    break
                }
            }
        }
    }

    private fun handlePredation() {
        val allPlayers = mutableListOf<GloopPlayer>()
        localPlayer?.let { if (it.isAlive) allPlayers.add(it) }
        allPlayers.addAll(remotePlayers.values.filter { it.isAlive })

        for (i in allPlayers.indices) {
            val playerA = allPlayers[i]
            for (j in (i + 1) until allPlayers.size) {
                val playerB = allPlayers[j]

                for (cellA in playerA.cells) {
                    for (cellB in playerB.cells) {
                        val decision = com.gloopgo.game.domain.PredationLogic.evaluate(
                            predatorPlayerId = playerA.id,
                            predatorCell = cellA,
                            preyPlayerId = playerB.id,
                            preyCell = cellB
                        )

                        if (decision is com.gloopgo.game.domain.PredationDecision.Consumed) {
                            val victimPlayer = if (localPlayer?.id == decision.victimPlayerId) localPlayer else remotePlayers[decision.victimPlayerId]
                            val isLastCell = (victimPlayer?.cells?.size ?: 1) <= 1
                            growCell(decision.eaterPlayerId, decision.eaterCellId, decision.massGained)
                            removeCell(decision.victimPlayerId, decision.victimCellId)

                            if (decision.eaterPlayerId == localPlayer?.id) {
                                predationEvents.add(
                                    PredationEvent(
                                        eaterPlayerId = decision.eaterPlayerId,
                                        eaterCellId = decision.eaterCellId,
                                        victimPlayerId = decision.victimPlayerId,
                                        victimCellId = decision.victimCellId,
                                        isLastCell = isLastCell,
                                        massGained = decision.massGained
                                    )
                                )
                            }
                        }
                    }
                }
            }
        }
    }

    private fun growCell(playerId: String, cellId: String, amount: Float) {
        if (localPlayer?.id == playerId) {
            localPlayer?.let { p ->
                val targetCell = p.cells.find { it.id == cellId } ?: return@let
                val newMass = targetCell.mass + amount
                if (newMass <= 0f) {
                    removeCell(playerId, cellId)
                } else {
                    localPlayer = p.copy(cells = p.cells.map { c ->
                        if (c.id == cellId) c.copy(mass = newMass) else c
                    })
                }
            }
        } else {
            val remote = remotePlayers[playerId]
            if (remote != null) {
                val targetCell = remote.cells.find { it.id == cellId } ?: return
                val newMass = targetCell.mass + amount
                if (newMass <= 0f) {
                    removeCell(playerId, cellId)
                } else {
                    remotePlayers[playerId] = remote.copy(
                        cells = remote.cells.map { c ->
                            if (c.id == cellId) c.copy(mass = newMass) else c
                        }
                    )
                }
            }
        }
    }

    private fun removeCell(playerId: String, cellId: String) {
        if (localPlayer?.id == playerId) {
            localPlayer?.let { p ->
                val remaining = p.cells.filter { it.id != cellId }
                if (remaining.isNotEmpty()) {
                    val updated = p.copy(cells = remaining)
                    localPlayer = updated
                    lastCameraTarget = updated.centerPosition
                    val playerMaxRadius = updated.maxRadius
                    val rawZoom = GameConstants.BASE_ZOOM / playerMaxRadius.pow(GameConstants.ZOOM_EXPONENT) * 3.5f
                    lastCameraZoom = rawZoom.coerceIn(GameConstants.MIN_ZOOM, GameConstants.MAX_ZOOM)
                } else {
                    localPlayer = p.copy(cells = emptyList(), isAlive = false)
                    isGameOver = true
                }
            }
        } else {
            val remote = remotePlayers[playerId]
            if (remote != null) {
                val remaining = remote.cells.filter { it.id != cellId }
                remotePlayers[playerId] = remote.copy(cells = remaining, isAlive = remaining.isNotEmpty())
            }
        }
    }

    /**
     * Splits all eligible cells of a player towards the given target direction.
     */
    fun splitPlayer(playerId: String, targetPos: Vector2D) {
        val player = if (localPlayer?.id == playerId) localPlayer else remotePlayers[playerId]
        if (player == null || !player.isAlive || !player.canSplit) return
        if (player.cells.size >= GameConstants.MAX_CELLS_PER_PLAYER) return

        val now = if (currentTimestamp > 0L) currentTimestamp else kotlin.time.Clock.System.now().toEpochMilliseconds()
        if (playerId == localPlayer?.id && now - lastSplitTimestamp < GameConstants.SPLIT_ACTION_COOLDOWN_MS) {
            return
        }

        val newCells = mutableListOf<GloopCell>()
        val recombineTime = now + GameConstants.SPLIT_COOLDOWN_MS
        var splitHappened = false

        for (cell in player.cells) {
            if (cell.mass >= GameConstants.SPLIT_MIN_MASS && (newCells.size + player.cells.size) < GameConstants.MAX_CELLS_PER_PLAYER) {
                splitHappened = true
                val halfMass = cell.mass / 2f
                val splitAngle = (targetPos - cell.position).angle()
                val impulse = Vector2D.fromAngle(splitAngle, GameConstants.SPLIT_IMPULSE_SPEED)

                // Original cell with half mass
                newCells.add(cell.copy(mass = halfMass, canRecombineAt = recombineTime))

                // Projected split cell
                val splitPos = cell.position + Vector2D.fromAngle(splitAngle, cell.radius * 0.75f)
                newCells.add(
                    GloopCell(
                        id = generateId("split-cell"),
                        position = splitPos,
                        velocity = impulse,
                        mass = halfMass,
                        canRecombineAt = recombineTime
                    )
                )
            } else {
                newCells.add(cell)
            }
        }

        if (splitHappened && playerId == localPlayer?.id) {
            lastSplitTimestamp = now
        }

        if (localPlayer?.id == playerId) {
            localPlayer = player.copy(cells = newCells)
        } else {
            remotePlayers[playerId] = player.copy(cells = newCells)
        }
    }

    /**
     * Ejects mass forward from player cells towards the given target.
     */
    fun ejectMass(playerId: String, targetPos: Vector2D) {
        val player = if (localPlayer?.id == playerId) localPlayer else remotePlayers[playerId]
        if (player == null || !player.isAlive || !player.canEjectMass) return

        val now = if (currentTimestamp > 0L) currentTimestamp else kotlin.time.Clock.System.now().toEpochMilliseconds()
        if (playerId == localPlayer?.id && now - lastEjectTimestamp < GameConstants.EJECT_ACTION_COOLDOWN_MS) {
            return
        }

        val updatedCells = mutableListOf<GloopCell>()
        var ejectHappened = false

        for (cell in player.cells) {
            if (cell.mass > GameConstants.EJECT_MIN_MASS) {
                ejectHappened = true
                val ejectAngle = (targetPos - cell.position).angle()
                val ejectDir = Vector2D.fromAngle(ejectAngle)
                val spawnPos = cell.position + ejectDir * (cell.radius + 15f)
                val ejectVel = ejectDir * GameConstants.EJECT_IMPULSE_SPEED

                val newMass = EjectedMass(
                    id = generateId("mass"),
                    ownerId = playerId,
                    position = spawnPos,
                    velocity = ejectVel,
                    color = player.color,
                    mass = GameConstants.EJECTED_MASS_VALUE,
                    createdAt = now
                )
                ejectedMasses.add(newMass)
                if (playerId == localPlayer?.id) {
                    newEjectedMasses.add(newMass)
                }

                updatedCells.add(cell.copy(mass = cell.mass - GameConstants.EJECTED_MASS_VALUE))
            } else {
                updatedCells.add(cell)
            }
        }

        if (ejectHappened && playerId == localPlayer?.id) {
            lastEjectTimestamp = now
        }

        if (localPlayer?.id == playerId) {
            localPlayer = player.copy(cells = updatedCells)
        } else {
            remotePlayers[playerId] = player.copy(cells = updatedCells)
        }
    }

    /**
     * Generates a snapshot of the current state for rendering.
     */
    fun getSnapshot(): GameState {
        val player = localPlayer
        if (player != null && player.cells.isNotEmpty()) {
            lastCameraTarget = player.centerPosition
            val playerMaxRadius = player.maxRadius
            val rawZoom = GameConstants.BASE_ZOOM / playerMaxRadius.pow(GameConstants.ZOOM_EXPONENT) * 3.5f
            lastCameraZoom = rawZoom.coerceIn(GameConstants.MIN_ZOOM, GameConstants.MAX_ZOOM)
        }

        val cameraTarget = lastCameraTarget
        val cameraZoom = lastCameraZoom

        // Build top 10 leaderboard
        val allPlayers = mutableListOf<GloopPlayer>()
        player?.let { if (it.isAlive) allPlayers.add(it) }
        allPlayers.addAll(remotePlayers.values.filter { it.isAlive })

        val leaderboard = allPlayers
            .sortedByDescending { it.totalMass }
            .take(10)
            .map { p ->
                LeaderboardEntry(
                    id = p.id,
                    name = if (p.emoji.isNotEmpty()) "${p.emoji} ${p.name}" else p.name,
                    score = p.totalMass.toInt(),
                    isLocalPlayer = p.id == player?.id
                )
            }

        val now = if (currentTimestamp > 0L) currentTimestamp else kotlin.time.Clock.System.now().toEpochMilliseconds()
        val splitRemaining = (lastSplitTimestamp + GameConstants.SPLIT_ACTION_COOLDOWN_MS - now).coerceAtLeast(0L)
        val ejectRemaining = (lastEjectTimestamp + GameConstants.EJECT_ACTION_COOLDOWN_MS - now).coerceAtLeast(0L)

        return GameState(
            localPlayer = player,
            remotePlayers = remotePlayers.values.filter { it.isAlive },
            pellets = pellets.toList(),
            ejectedMasses = ejectedMasses.toList(),
            isGameOver = isGameOver,
            leaderboard = leaderboard,
            cameraTarget = cameraTarget,
            cameraZoom = cameraZoom,
            splitCooldownRemainingMs = splitRemaining,
            ejectCooldownRemainingMs = ejectRemaining
        )
    }

    /**
     * Shifts time-sensitive timers (cooldowns, bot respawn delays, ejected mass age)
     * by the duration the game was paused, preventing elapsed pause time from advancing game timers.
     */
    fun adjustTimestampsForPause(pauseDurationMs: Long) {
        if (pauseDurationMs <= 0L) return
        if (currentTimestamp > 0L) currentTimestamp += pauseDurationMs
        if (lastSplitTimestamp > 0L) lastSplitTimestamp += pauseDurationMs
        if (lastEjectTimestamp > 0L) lastEjectTimestamp += pauseDurationMs
        localPlayer?.let { player ->
            localPlayer = player.copy(
                cells = player.cells.map { cell ->
                    if (cell.canRecombineAt > 0L) cell.copy(canRecombineAt = cell.canRecombineAt + pauseDurationMs) else cell
                }
            )
        }
        for ((id, bot) in remotePlayers) {
            remotePlayers[id] = bot.copy(
                lastHeartbeat = if (bot.lastHeartbeat > 0L) bot.lastHeartbeat + pauseDurationMs else 0L,
                cells = bot.cells.map { cell ->
                    if (cell.canRecombineAt > 0L) cell.copy(canRecombineAt = cell.canRecombineAt + pauseDurationMs) else cell
                }
            )
        }
        for (key in aiRespawnAtMs.keys.toList()) {
            val at = aiRespawnAtMs[key]
            if (at != null) {
                aiRespawnAtMs[key] = at + pauseDurationMs
            }
        }
        for (i in ejectedMasses.indices) {
            val em = ejectedMasses[i]
            if (em.createdAt > 0L) {
                ejectedMasses[i] = em.copy(createdAt = em.createdAt + pauseDurationMs)
            }
        }
    }
}
